import { useState } from "react";
import {
  Settings, Briefcase, Clock, Calendar, Sun, FolderOpen, FileText,
  Plus, Upload, Download, Trash2, X, Save, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import DateInput from "../DateInput";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";

const TIPI_CONTRATTO = [
  { value: "full_time",     label: "Full Time",        ore: 40, ferie: 26, lordo_sep: 1.44, lordo_busta: 1.32, costo_sep: 1.88, costo_busta: 1.72 },
  { value: "part_time_30",  label: "Part Time 30h",    ore: 30, ferie: 26, lordo_sep: 1.36, lordo_busta: 1.26, costo_sep: 1.77, costo_busta: 1.64 },
  { value: "part_time_24",  label: "Part Time 24h",    ore: 24, ferie: 26, lordo_sep: 1.30, lordo_busta: 1.22, costo_sep: 1.69, costo_busta: 1.58 },
  { value: "part_time_20",  label: "Part Time 20h",    ore: 20, ferie: 26, lordo_sep: 1.28, lordo_busta: 1.20, costo_sep: 1.66, costo_busta: 1.56 },
  { value: "apprendistato", label: "Apprendistato",    ore: 40, ferie: 26, lordo_sep: 1.24, lordo_busta: 1.16, costo_sep: 1.46, costo_busta: 1.37 },
  { value: "stagionale",    label: "Stagionale",       ore: 40, ferie: 0,  lordo_sep: 1.44, lordo_busta: 1.32, costo_sep: 1.82, costo_busta: 1.67 },
  { value: "chiamata",      label: "A Chiamata",       ore: 0,  ferie: 0,  lordo_sep: 1.28, lordo_busta: 1.20, costo_sep: 1.66, costo_busta: 1.56 },
];

const GIORNI_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export const PreferenzeTurniDialog = ({
  open, onOpenChange, operatoreSelezionato, prefForm, setPrefForm,
  onSave, saving, fasce, toggleGiornoRiposoDisponibile, toggleTurnoEscluso
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-primary" />
          Preferenze Turni - {operatoreSelezionato?.nome || operatoreSelezionato?.email || operatoreSelezionato?.telefono}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-5 py-2">
        <div>
          <Label className="text-sm font-medium">Quanti giorni di riposo a settimana?</Label>
          <div className="flex gap-2 mt-2">
            {[1, 2, 3, 4, 5, 6].map(n => (
              <button key={n} onClick={() => setPrefForm({ ...prefForm, giorni_riposo: n })}
                className={cn("flex-1 py-2.5 rounded-lg text-sm font-bold transition-all",
                  prefForm.giorni_riposo === n ? "bg-red-500 text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}>
                {n}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            {7 - (prefForm.giorni_riposo || 1)} giorni lavorativi obbligatori
          </p>
        </div>

        <div>
          <Label className="text-sm font-medium">Quali giorni PUÒ riposare?</Label>
          <p className="text-xs text-muted-foreground mb-2">Seleziona i giorni candidati per il riposo</p>
          <div className="grid grid-cols-7 gap-1">
            {GIORNI_SHORT.map((g, idx) => {
              const isDisponibile = prefForm.giorni_riposo_disponibili?.includes(idx);
              return (
                <button key={idx} onClick={() => toggleGiornoRiposoDisponibile(idx)}
                  className={cn("py-2.5 rounded-lg text-xs font-bold transition-all",
                    isDisponibile ? "bg-amber-500 text-white" : "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
                  )}>
                  {g}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-4 mt-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500"></span>Può riposare</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500"></span>Obbligatorio</span>
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium">Turni che NON può fare</Label>
          <p className="text-xs text-muted-foreground mb-2">Opzionale - Esclude da certi turni</p>
          <div className="space-y-2">
            {fasce.map(fascia => {
              const isEscluso = prefForm.turni_esclusi?.includes(fascia.id);
              return (
                <button key={fascia.id} onClick={() => toggleTurnoEscluso(fascia.id)}
                  className={cn("w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left",
                    isEscluso ? "border-red-400 bg-red-50 dark:bg-red-900/20" : "border-border bg-card hover:border-muted-foreground/30"
                  )}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: fascia.colore || '#6C63FF' }} />
                    <span className={cn("font-medium", isEscluso && "text-red-600 dark:text-red-400")}>{fascia.nome}</span>
                  </div>
                  <span className={cn("text-xs", isEscluso ? "text-red-500" : "text-muted-foreground")}>
                    {fascia.ora_inizio} - {fascia.ora_fine}
                  </span>
                  {isEscluso && <X className="h-4 w-4 text-red-500" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Annulla</Button>
        <Button onClick={onSave} disabled={saving} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-medium">
          {saving ? "Salvataggio..." : "Salva"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ContrattoDialog = ({
  open, onOpenChange, operatoreSelezionato, contrattoForm, setContrattoForm,
  onTipoContrattoChange, onSave, saving,
  contrattiAllegati, loadingContratti, onUploadContratto, onDeleteContratto, API
}) => {
  const [showAllegatoForm, setShowAllegatoForm] = useState(false);
  const [allegatoForm, setAllegatoForm] = useState({ data_inizio: "", data_scadenza: "", file: null, file_name: "" });
  const [uploadingAllegato, setUploadingAllegato] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { return; }
    const reader = new FileReader();
    reader.onload = () => {
      setAllegatoForm(prev => ({ ...prev, file: reader.result, file_name: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveAllegato = async () => {
    if (!allegatoForm.file) return;
    setUploadingAllegato(true);
    try {
      await onUploadContratto({
        dipendente_id: operatoreSelezionato.id,
        nome: allegatoForm.file_name,
        data_inizio: allegatoForm.data_inizio || null,
        data_scadenza: allegatoForm.data_scadenza || null,
        file_data: allegatoForm.file,
        file_name: allegatoForm.file_name
      });
      setShowAllegatoForm(false);
      setAllegatoForm({ data_inizio: "", data_scadenza: "", file: null, file_name: "" });
    } finally {
      setUploadingAllegato(false);
    }
  };

  const handleDownloadAllegato = async (allegato) => {
    const token = (window.localStorage && window.localStorage.getItem("token")) || "";
    const baseUrl = `${(API || '').replace('/api', '')}${allegato.file_url}`;
    const fileUrl = token ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}` : baseUrl;
    const fileName = allegato.file_nome || allegato.nome || 'contratto.pdf';
    if (navigator.share) {
      try {
        const res = await fetch(fileUrl);
        const blob = await res.blob();
        const file = new File([blob], fileName, { type: blob.type });
        await navigator.share({ files: [file], title: fileName });
        return;
      } catch {}
    }
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = fileName;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" />
            Contratto - {operatoreSelezionato?.nome || operatoreSelezionato?.email || operatoreSelezionato?.telefono}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Tipo Contratto */}
          <div className="space-y-2">
            <Label>Tipo Contratto</Label>
            <Select value={contrattoForm.tipo_contratto} onValueChange={onTipoContrattoChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPI_CONTRATTO.map(t => (<SelectItem key={t.value} value={t.value}>{t.label} ({t.ore}h/sett)</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          {/* Stipendio netto + 13a + calcolo lordo */}
          <div className="space-y-3 rounded-xl border border-border bg-muted/10 p-3">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Stipendio mensile netto (€)
              </Label>
              <Input
                type="number"
                min="0"
                step="50"
                placeholder="es. 1500"
                value={contrattoForm.stipendio_mensile_netto || ""}
                onChange={(e) => setContrattoForm(prev => ({ ...prev, stipendio_mensile_netto: e.target.value === "" ? null : parseFloat(e.target.value) }))}
                data-testid="input-stipendio-mensile-netto"
              />
            </div>
            {/* Checkbox 13a */}
            <div className="flex items-center gap-2.5">
              <Checkbox
                id="tredicesima-in-busta-scheda"
                checked={contrattoForm.tredicesima_in_busta || false}
                onCheckedChange={(v) => setContrattoForm(prev => ({ ...prev, tredicesima_in_busta: !!v }))}
                data-testid="checkbox-tredicesima-in-busta"
              />
              <Label htmlFor="tredicesima-in-busta-scheda" className="text-sm text-foreground/80 cursor-pointer">
                13ª inclusa nella busta paga mensile
              </Label>
            </div>
            {/* Calcolo lordo */}
            {contrattoForm.stipendio_mensile_netto > 0 && (() => {
              const t = TIPI_CONTRATTO.find(c => c.value === contrattoForm.tipo_contratto);
              if (!t) return null;
              const k = contrattoForm.tredicesima_in_busta;
              const lordoMens = contrattoForm.stipendio_mensile_netto * (k ? t.lordo_busta : t.lordo_sep);
              const costoMens = contrattoForm.stipendio_mensile_netto * (k ? t.costo_busta : t.costo_sep);
              return (
                <div className="text-xs bg-blue-500/8 border border-blue-500/20 rounded-lg px-3 py-2 space-y-1" data-testid="moltiplicatore-info">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Lordo mensile stimato:</span>
                    <span className="font-semibold text-foreground">€ {lordoMens.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Costo aziendale mensile:</span>
                    <span className="font-semibold text-foreground">€ {costoMens.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Labor Cost annuale:</span>
                    <span className="font-semibold text-foreground">€ {(costoMens * 12).toFixed(0)}</span>
                  </div>
                  <p className="text-muted-foreground/60 mt-1">
                    {k ? "13ª spalmata sul mensile." : "13ª come mensilità aggiuntiva."} Stime CCNL Turismo — confrontare con CDL.
                  </p>
                </div>
              );
            })()}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Clock className="h-4 w-4" />Ore Settimanali</Label>
              <Input type="number" min="0" max="60" value={contrattoForm.ore_settimanali} onChange={(e) => setContrattoForm(prev => ({ ...prev, ore_settimanali: parseInt(e.target.value) || 0 }))} />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Sun className="h-4 w-4" />Ferie Annuali</Label>
              <Input type="number" min="0" max="50" value={contrattoForm.giorni_ferie_annuali} onChange={(e) => setContrattoForm(prev => ({ ...prev, giorni_ferie_annuali: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Ferie Residue (gg)</Label>
            <Input type="number" min="0" step="0.5" value={contrattoForm.giorni_ferie_residui} onChange={(e) => setContrattoForm(prev => ({ ...prev, giorni_ferie_residui: parseFloat(e.target.value) || 0 }))} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Calendar className="h-4 w-4" />Data Assunzione</Label>
              <DateInput value={contrattoForm.data_assunzione} onChange={(v) => setContrattoForm(prev => ({ ...prev, data_assunzione: v }))} />
            </div>
            <div className="space-y-2">
              <Label>Scadenza</Label>
              <DateInput value={contrattoForm.data_fine_contratto} onChange={(v) => setContrattoForm(prev => ({ ...prev, data_fine_contratto: v }))} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Note Contratto</Label>
            <Input value={contrattoForm.note_contratto} onChange={(e) => setContrattoForm(prev => ({ ...prev, note_contratto: e.target.value }))} placeholder="Eventuali note..." />
          </div>

          {/* === ALLEGATI CONTRATTO === */}
          <div className="pt-3 border-t border-border space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Allegati Contratto</Label>
              {!showAllegatoForm && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAllegatoForm(true)}
                  className="gap-1 h-8 text-xs active:scale-95"
                  data-testid="add-contratto-allegato-btn"
                >
                  <Plus className="h-3.5 w-3.5" /> Aggiungi
                </Button>
              )}
            </div>

            {/* Form inline per nuovo allegato */}
            {showAllegatoForm && (
              <div className="p-3 rounded-xl bg-muted/50 border border-border space-y-3">
                {allegatoForm.file_name ? (
                  <div className="flex items-center gap-2 p-2 bg-primary/10 rounded-lg">
                    <FileText className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-xs font-medium truncate flex-1">{allegatoForm.file_name}</span>
                    <button onClick={() => setAllegatoForm(prev => ({ ...prev, file: null, file_name: "" }))} className="text-red-500">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                    <Upload className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Seleziona file contratto</span>
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileSelect} data-testid="contratto-file-input" />
                  </label>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Data Inizio</Label>
                    <DateInput value={allegatoForm.data_inizio} onChange={(v) => setAllegatoForm(prev => ({ ...prev, data_inizio: v }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Scadenza</Label>
                    <DateInput value={allegatoForm.data_scadenza} onChange={(v) => setAllegatoForm(prev => ({ ...prev, data_scadenza: v }))} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setShowAllegatoForm(false); setAllegatoForm({ data_inizio: "", data_scadenza: "", file: null, file_name: "" }); }} className="flex-1 h-9 text-xs">Annulla</Button>
                  <Button size="sm" onClick={handleSaveAllegato} disabled={!allegatoForm.file || uploadingAllegato} className="flex-1 h-9 text-xs active:scale-95" data-testid="save-contratto-allegato-btn">
                    {uploadingAllegato ? "Caricamento..." : "Salva"}
                  </Button>
                </div>
              </div>
            )}

            {/* Lista allegati */}
            {loadingContratti ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
              </div>
            ) : contrattiAllegati && contrattiAllegati.length > 0 ? (
              <div className="space-y-2">
                {contrattiAllegati.map(allegato => (
                  <div key={allegato.id} className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border" data-testid={`contratto-allegato-${allegato.id}`}>
                    {allegato.file_url ? (
                      <button
                        onClick={() => handleDownloadAllegato(allegato)}
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-primary text-white hover:bg-primary/90 active:scale-95 transition-all shadow-sm"
                        data-testid={`download-contratto-${allegato.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    ) : (
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-muted">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{allegato.nome || allegato.file_nome}</p>
                      {(allegato.data_inizio || allegato.data_scadenza) && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {allegato.data_inizio && new Date(allegato.data_inizio).toLocaleDateString("it-IT")}
                          {allegato.data_inizio && allegato.data_scadenza && " → "}
                          {allegato.data_scadenza && new Date(allegato.data_scadenza).toLocaleDateString("it-IT")}
                        </p>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-red-500 hover:bg-red-500/10" onClick={() => onDeleteContratto(allegato.id)} data-testid={`delete-contratto-${allegato.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-2">Nessun allegato</p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Annulla</Button>
          <Button onClick={onSave} disabled={saving} className="flex-1 bg-primary">
            <Save className="h-4 w-4 mr-2" />{saving ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DocumentiDialog = ({
  open, onOpenChange, operatoreSelezionato,
  documentiOperatore, loadingDocumenti,
  showNuovoDocForm, setShowNuovoDocForm,
  nuovoDocForm, setNuovoDocForm,
  onCreaDocumento, onUploadFileDoc, onDeleteDoc, saving, API, token
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FolderOpen className="h-5 w-5 text-primary" />
          Documenti - {operatoreSelezionato?.nome || operatoreSelezionato?.email || operatoreSelezionato?.telefono}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4 py-2">
        {!showNuovoDocForm && (
          <Button onClick={() => setShowNuovoDocForm(true)} variant="outline" className="w-full gap-2" data-testid="add-document-btn">
            <Plus className="h-4 w-4" />Aggiungi Documento
          </Button>
        )}

        {showNuovoDocForm && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-4 space-y-3">
              <div>
                <Label>Nome Documento *</Label>
                <Input value={nuovoDocForm.nome} onChange={(e) => setNuovoDocForm(prev => ({ ...prev, nome: e.target.value }))} placeholder="es. Contratto, Attestato HACCP, Certificato medico..." data-testid="doc-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data Documento</Label>
                  <DateInput value={nuovoDocForm.data_documento} onChange={(v) => setNuovoDocForm(prev => ({ ...prev, data_documento: v }))} />
                </div>
                <div>
                  <Label>Data Scadenza</Label>
                  <DateInput value={nuovoDocForm.data_scadenza} onChange={(v) => setNuovoDocForm(prev => ({ ...prev, data_scadenza: v }))} />
                </div>
              </div>
              <div>
                <Label>Note</Label>
                <Input value={nuovoDocForm.note} onChange={(e) => setNuovoDocForm(prev => ({ ...prev, note: e.target.value }))} placeholder="Note opzionali..." />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => { setShowNuovoDocForm(false); setNuovoDocForm({ nome: "", data_documento: "", data_scadenza: "", note: "" }); }} className="flex-1">Annulla</Button>
                <Button onClick={onCreaDocumento} disabled={saving || !nuovoDocForm.nome.trim()} className="flex-1" data-testid="save-document-btn">
                  {saving ? "Salvataggio..." : "Crea"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loadingDocumenti ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : documentiOperatore.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>Nessun documento</p>
          </div>
        ) : (
          <div className="space-y-2">
            {documentiOperatore.map(doc => {
              const isScaduto = doc.data_scadenza && new Date(doc.data_scadenza) < new Date();
              const isInScadenza = doc.data_scadenza && !isScaduto && (new Date(doc.data_scadenza) - new Date()) / (1000 * 60 * 60 * 24) <= 30;

              return (
                <Card key={doc.id} className={cn("hover:bg-muted/30 transition-colors", isScaduto && "border-red-500/50 bg-red-500/5", isInScadenza && "border-orange-500/50 bg-orange-500/5")} data-testid={`doc-card-${doc.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      {doc.file_url ? (
                        <button
                          type="button"
                          onClick={async () => {
                            const token = (window.localStorage && window.localStorage.getItem("token")) || "";
                            const baseUrl = `${API.replace('/api', '')}${doc.file_url}`;
                            const fileUrl = token ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}` : baseUrl;
                            const fileName = doc.nome + (doc.file_url.substring(doc.file_url.lastIndexOf('.')) || '');
                            if (navigator.share) {
                              try {
                                const res = await fetch(fileUrl);
                                const blob = await res.blob();
                                const file = new File([blob], fileName, { type: blob.type });
                                await navigator.share({ files: [file], title: doc.nome });
                                return;
                              } catch {}
                            }
                            const a = document.createElement('a');
                            a.href = fileUrl;
                            a.download = fileName;
                            a.target = '_blank';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-primary text-white hover:bg-primary/90 transition-colors active:scale-95 shadow-sm"
                          title="Scarica / Condividi"
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download className="h-5 w-5" />
                        </button>
                      ) : (
                        <label className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-muted hover:bg-muted/80 cursor-pointer text-muted-foreground hover:text-primary transition-colors" title="Carica file">
                          <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,image/*" onChange={(e) => onUploadFileDoc(e, doc.id)} data-testid={`upload-doc-${doc.id}`} />
                          <Upload className="h-5 w-5" />
                        </label>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground text-sm truncate">{doc.nome}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {doc.data_documento && (<span className="text-xs text-muted-foreground">{new Date(doc.data_documento).toLocaleDateString("it-IT")}</span>)}
                          {isScaduto && (<Badge className="bg-red-500 text-white text-[10px] px-1.5">Scaduto</Badge>)}
                          {isInScadenza && (<Badge className="bg-orange-500 text-white text-[10px] px-1.5">In scadenza</Badge>)}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={() => onDeleteDoc(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">Chiudi</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
